import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Clock, TrendingUp, Calendar, Package, Users, Wrench, Warehouse, Plus, Trash2, Receipt, Upload, Tag, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RegimeBadge } from '@/components/RegimeBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function OngletStatistiques({ dossier }) {
  const { isProprietaire } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [produits, setProduits] = useState([]);
  const [punches, setPunches] = useState([]);
  const [materiauxEntrepot, setMateriauxEntrepot] = useState({ movements: [], cout_total_materiaux: 0 });
  const [fraisMateriaux, setFraisMateriaux] = useState(dossier.frais_materiaux || []);
  const [showAddFrais, setShowAddFrais] = useState(false);
  const [fraisForm, setFraisForm] = useState({ montant: '', date: new Date().toISOString().split('T')[0], description: '', facture_url: '' });
  const [savingFrais, setSavingFrais] = useState(false);
  const [uploadingFacture, setUploadingFacture] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorieActuelle, setCategorieActuelle] = useState(dossier.categorie_travaux || '');
  const [savingCategorie, setSavingCategorie] = useState(false);
  const [objectifsMarge, setObjectifsMarge] = useState({
    objectif_employes_pct: 30,
    objectif_peinture_pct: 10,
    objectif_materiaux_pct: 5,
    objectif_marge_brute_min: 40,
    objectif_marge_brute_ideal: 55,
  });

  // Historique manuel
  const [heuresManuelles, setHeuresManuelles] = useState([]);
  const [produitsManuels, setProduitsManuels] = useState([]);
  const [commandesBonsTravail, setCommandesBonsTravail] = useState([]);
  const [bonsTravailFetched, setBonsTravailFetched] = useState([]); // BTs chargés depuis l'API
  // 🆕 [Phase 53] Agrégation pour dossiers liés (jumelés). Si un des BT a un
  // `dossiers_lies.length > 1`, on fusionne les soumissions/factures/BT des
  // autres dossiers liés pour que Valeur Contrat / Facturé / Payé / Production
  // / Rentabilité reflètent le TOTAL combiné.
  const [dossiersLiesData, setDossiersLiesData] = useState([]);  // [{id, nom, soumissions, factures, bons_travail}]
  // 🚨 [HOTFIX 2026-06-09] Variable computed : vrai dès qu'on a au moins un dossier lié
  // (Phase 53 avait introduit `isAggregated` dans le JSX du badge `Stats combinées` sans le déclarer
  // → `ReferenceError: isAggregated is not defined` en prod sur l'onglet Stats).
  const isAggregated = dossiersLiesData.length > 0;
  const [showHeuresSection, setShowHeuresSection] = useState(false);
  const [showProduitsSection, setShowProduitsSection] = useState(false);
  const [showFraisSection, setShowFraisSection] = useState(false);
  const [showAddProduit, setShowAddProduit] = useState(false);
  const [produitForm, setProduitForm] = useState({ produit_id: '', quantite: '', date: new Date().toISOString().split('T')[0], notes: '' });
  const [savingProduit, setSavingProduit] = useState(false);
  // Modals de détail cliquables
  const [showDetailProduits, setShowDetailProduits] = useState(false);
  const [showDetailMO, setShowDetailMO] = useState(false);

  useEffect(() => { fetchData(); }, [dossier.id]);

  const fetchData = async () => {
    try {
      // 🆕 [Phase 31 #7] include_inactive=true — Le détail Main d'œuvre est une vue
      // HISTORIQUE : les pointages d'employés devenus inactifs doivent garder leur taux.
      // Sans ce flag, les pointages d'inactifs affichent « Taux manquant » 0,00$.
      const [eRes, pRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees?include_inactive=true`),
        axios.get(`${API_URL}/api/produits`),
        axios.get(`${API_URL}/api/crm/categories-travaux`),
      ]);
      if (eRes.status === 200) { const d = eRes.data; setEmployees(d.employees || []); }
      if (pRes.status === 200) { const d = pRes.data; setProduits(d.produits || []); }
      if (catRes.status === 200) { const d = catRes.data; setCategories(d.categories || []); }

      const mRes = await axios.get(`${API_URL}/api/inventory/movements/projet/${dossier.id}`);
      if (mRes.status === 200) { const d = mRes.data; setMateriauxEntrepot(d); }

      // ✅ Charger les BTs depuis l'API (plus fiable que dossier.bons_travail du prop)
      const bonsRes = await axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${dossier.id}`);
      const fetchedBons = bonsRes.data?.bons_travail || dossier.bons_travail || [];
      setBonsTravailFetched(fetchedBons);

      // 🆕 [Phase 53] Détecter les dossiers LIÉS (jumelés) sur n'importe quel BT
      // et fetcher leurs données complètes pour agréger Valeur Contrat / Facturé /
      // Payé / Production / Rentabilité. Best-effort : si un fetch échoue, on
      // continue avec ce qu'on a (pas de régression).
      try {
        const dossiersLiesIds = new Set();
        for (const bon of fetchedBons) {
          for (const dl of (bon.dossiers_lies || [])) {
            if (dl.dossier_id && dl.dossier_id !== dossier.id) {
              dossiersLiesIds.add(dl.dossier_id);
            }
          }
        }
        if (dossiersLiesIds.size > 0) {
          const liesData = await Promise.all(
            [...dossiersLiesIds].map(async (did) => {
              try {
                const res = await axios.get(`${API_URL}/api/crm/dossiers/${did}`);
                const d = res.data?.dossier || res.data || {};
                // Charger les BT du dossier lié séparément
                const bres = await axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${did}`);
                d.bons_travail = bres.data?.bons_travail || d.bons_travail || [];
                return {
                  id: did,
                  nom: `${d.client_prenom || ''} ${d.client_nom || ''}`.trim() || d.nom_dossier || did,
                  soumissions: d.soumissions || [],
                  factures: d.factures || [],
                  bons_travail: d.bons_travail || [],
                };
              } catch (e) {
                return null;
              }
            })
          );
          setDossiersLiesData(liesData.filter(Boolean));
        } else {
          setDossiersLiesData([]);
        }
      } catch (e) {
        setDossiersLiesData([]);
      }

      const allPunches = [];
      const allCommandesProduits = [];
      // 🆕 2026-05-06 Phase 6 Module CCQ — map BT id → regime pour enrichir les punches localement
      const btRegimeMap = {};
      for (const bon of fetchedBons) {
        btRegimeMap[bon.id] = bon.regime_ccq || 'hors_ccq';
        const res = await axios.get(`${API_URL}/api/punch/bon-travail/${bon.id}`);
        if (res.status === 200) {
          const d = res.data;
          (d.punches || []).forEach(p => { p.bt_regime_ccq = btRegimeMap[bon.id]; });
          allPunches.push(...(d.punches || []));
        }
        // Collecter les commandes pour les stats
        for (const cmd of (bon.historique_commandes || [])) {
          for (const p of (cmd.produits || [])) {
            allCommandesProduits.push({
              ...p,
              date: cmd.date,
              commandee_par: cmd.commandee_par,
              bon_id: bon.id,
            });
          }
        }
      }
      setPunches(allPunches);
      setCommandesBonsTravail(allCommandesProduits);

      // Reload frais matériaux
      const fRes = await axios.get(`${API_URL}/api/crm/dossiers/${dossier.id}/frais-materiaux`);
      if (fRes.status === 200) { const d = fRes.data; setFraisMateriaux(d.frais_materiaux || []); }

      // Charger heures manuelles et produits manuels
      const [hmRes, pmRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/dossiers/${dossier.id}/heures-manuelles`),
        axios.get(`${API_URL}/api/crm/dossiers/${dossier.id}/produits-manuels`),
      ]);
      if (hmRes.status === 200) { const d = hmRes.data; setHeuresManuelles(d.heures_manuelles || []); }
      if (pmRes.status === 200) { const d = pmRes.data; setProduitsManuels(d.produits_manuels || []); }

      // Charger objectifs de marge
      const oRes = await axios.get(`${API_URL}/api/configuration/objectifs-marge`);
      if (oRes.status === 200) { const d = oRes.data; setObjectifsMarge(prev => ({ ...prev, ...d })); }
    } catch (e) { console.error(e); }
  };

  const saveCategorie = async (val) => {
    setSavingCategorie(true);
    try {
      const res = await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/categorie`, 
        { categorie_travaux: val === '__aucune__' ? null : val },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (res.status === 200) { setCategorieActuelle(val === '__aucune__' ? '' : val); toast.success('Catégorie mise à jour'); }
      else { const d = res.data; toast.error(d.detail || 'Erreur'); }
    } catch { toast.error('Erreur réseau'); }
    finally { setSavingCategorie(false); }
  };

  const addFrais = async () => {
    if (!fraisForm.montant || parseFloat(fraisForm.montant) <= 0) { toast.error('Montant invalide'); return; }
    setSavingFrais(true);
    try {
      const res = await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/frais-materiaux`, { 
        ...fraisForm, 
        montant: parseFloat(fraisForm.montant) 
      });
      if (res.status !== 200) throw new Error();
      const d = res.data;
      setFraisMateriaux(prev => [...prev, d.frais]);
      setShowAddFrais(false);
      setFraisForm({ montant: '', date: new Date().toISOString().split('T')[0], description: '', facture_url: '' });
      toast.success('Frais ajouté!');
    } catch { toast.error('Erreur ajout frais'); }
    finally { setSavingFrais(false); }
  };

  const deleteFrais = async (fraisId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/crm/dossiers/${dossier.id}/frais-materiaux/${fraisId}`);
      if (res.status !== 200) throw new Error();
      setFraisMateriaux(prev => prev.filter(f => f.id !== fraisId));
      toast.success('Frais supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  // Heures manuelles
  const getCoutHoraireADate = (emp, dateStr) => {
    const historique = (emp?.historique_salaires || []).filter(h => h.date_effective);
    if (historique.length > 0) {
      const active = historique.filter(h => {
        const de = (h.date_effective || '').substring(0, 10);
        const df = (h.date_fin || '').substring(0, 10);
        return de && de <= dateStr && (!df || df >= dateStr);
      });
      if (active.length > 0) {
        const best = active.reduce((a, b) =>
          (a.date_effective || '') >= (b.date_effective || '') ? a : b);
        return best.cout_employeur || best.nouveau_taux || best.hors_decret || 0;
      }
    }
    return emp?.salaires?.cout_employeur || emp?.salaires?.hors_decret || 0;
  };

  // 🗑️ 2026-05-13 — Fonction `addHeuresManuelles` supprimée : la saisie
  //   d'heures passe désormais UNIQUEMENT par Validation des heures →
  //   Importer des heures (canal unique, traçable, lié aux punchs).
  //   La fonction `deleteHeuresManuelles` ci-dessous est conservée pour
  //   permettre la suppression d'anciennes entrées historiques.

  const deleteHeuresManuelles = async (entryId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/crm/dossiers/${dossier.id}/heures-manuelles/${entryId}`);
      if (res.status !== 200) throw new Error();
      setHeuresManuelles(prev => prev.filter(h => h.id !== entryId));
      toast.success('Entrée supprimée');
    } catch { toast.error('Erreur suppression'); }
  };

  // Produits manuels
  const addProduitManuel = async () => {
    const prod = produits.find(p => p.id === produitForm.produit_id);
    if (!prod) { toast.error('Sélectionnez un produit'); return; }
    if (!produitForm.quantite || parseFloat(produitForm.quantite) <= 0) { toast.error('Quantité invalide'); return; }
    setSavingProduit(true);
    try {
      const prixUnit = prod?.prix_paye || 0;
      const res = await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/produits-manuels`, {
        produit_id: prod.id,
        produit_nom: prod.nom || prod.name,
        quantite: parseFloat(produitForm.quantite),
        date: produitForm.date,
        notes: produitForm.notes || '',
        prix_unitaire: prixUnit
      });
      if (res.status !== 200) throw new Error();
      const d = res.data;
      setProduitsManuels(prev => [...prev, d.entry]);
      setShowAddProduit(false);
      setProduitForm({ produit_id: '', quantite: '', date: new Date().toISOString().split('T')[0], notes: '' });
      toast.success('Produit ajouté!');
    } catch { toast.error('Erreur ajout produit'); }
    finally { setSavingProduit(false); }
  };

  const deleteProduitManuel = async (entryId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/crm/dossiers/${dossier.id}/produits-manuels/${entryId}`);
      if (res.status !== 200) throw new Error();
      setProduitsManuels(prev => prev.filter(p => p.id !== entryId));
      toast.success('Produit supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  const uploadFacture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFacture(true);
    try {
      const { compressImageIfNeeded } = await import('@/lib/imageCompression');
      const compressed = await compressImageIfNeeded(file);
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('category', 'facture_materiau');
      const res = await axios.post(`${API_URL}/api/upload`, formData);
      if (res.status === 200) {
        const d = res.data;
        setFraisForm(f => ({ ...f, facture_url: d.url || d.file_url || '' }));
        toast.success('Facture uploadée!');
      }
    } catch { toast.error('Erreur upload'); }
    finally { setUploadingFacture(false); }
  };

  const soumissions = dossier.soumissions || [];
  const factures = dossier.factures || [];
  // ✅ Utiliser les BTs chargés depuis l'API (corriger le bug "0 bons de travail")
  const bonsTravail = bonsTravailFetched.length > 0 ? bonsTravailFetched : (dossier.bons_travail || []);

  // ✅ CORRECTION: Utiliser montant_ht (sans taxes) pour les calculs de stats
  const montantContratTTC = soumissions.reduce((sum, s) => sum + (s.montant_final || 0), 0);
  const montantContrat = soumissions.reduce((sum, s) => {
    // Essayer d'utiliser montant_ht si disponible, sinon calculer en divisant par 1.14975 (TPS+TVQ QC)
    const ht = s.montant_ht || (s.montant_final ? s.montant_final / 1.14975 : 0);
    return sum + ht;
  }, 0);
  const montantFacture = factures.reduce((sum, f) => sum + (f.montant_total || 0), 0);
  const montantPaye = factures.reduce((sum, f) => sum + (f.montant_paye || 0), 0);
  const montantDu = factures.reduce((sum, f) => sum + (f.solde_du || 0), 0);

  const heuresBudgetees = bonsTravail.reduce((sum, b) => sum + (b.heures_budgetees || 0), 0);
  const heuresReelles = bonsTravail.reduce((sum, b) => sum + (b.heures_reelles || 0), 0);
  const tauxRealisation = heuresBudgetees > 0 ? (heuresReelles / heuresBudgetees) * 100 : 0;

  // Coût produits commandés
  const allProduitsBon = bonsTravail.flatMap(b => b.produits || []);
  const coutProduits = allProduitsBon.reduce((sum, p) => {
    const prod = produits.find(pr => pr.id === p.produit_id);
    const prixUnit = prod?.prix_paye || 0;
    return sum + (prixUnit * (p.quantite || 0));
  }, 0);
  // Ajouter les produits manuels
  const coutProduitsManuels = produitsManuels.reduce((sum, p) => sum + (p.cout_total || 0), 0);
  // Ajouter les commandes depuis les bons de travail
  const coutCommandesBons = commandesBonsTravail.reduce((sum, p) => {
    // Récupérer le prix D'ACHAT depuis la table produits (prix payé au fournisseur)
    const produitData = produits.find(pr => pr.id === p.produit_id);
    // 🐛 [Phase 94 — 2026-07-08] Exception E3 accordée : priorité au snapshot commande
    //   `p.prix_paye_snapshot` (backfillé pour les commandes historiques via migration
    //   Phase 94) OU `p.prix_paye` (snapshot temps réel côté backend) — évite le prix
    //   du catalogue quand l'unité de la commande ne match plus le produit référencé.
    const prix = parseFloat(p.prix_paye_snapshot || p.prix_paye || p.prix_unitaire || produitData?.prix_paye || 0);
    const qte = parseFloat(p.quantite_commandee || p.quantite || 0);
    return sum + (prix * qte);
  }, 0);
  // ✅ CORRECTION: Éviter le double comptage
  // Si des commandes existent, utiliser SEULEMENT les commandes (réalité)
  // Sinon, utiliser les produits initiaux (prévisionnel)
  const hasCommandes = commandesBonsTravail.length > 0;
  const coutProduitsReel = hasCommandes ? coutCommandesBons : coutProduits;
  const coutProduitsTotaux = coutProduitsReel + coutProduitsManuels;
  const pctProduits = montantContrat > 0 ? (coutProduitsTotaux / montantContrat * 100) : 0;

  // Coût main d'œuvre (heures punchées × coût employeur historique)
  // ✅ FIX (2026-04-20): Exclure les punches refusés des stats/main d'œuvre.
  // Seuls les punches validés/approuvés/auto (ou complete non-refusés) sont comptés.
  const completedPunches = punches.filter(p =>
    p.statut !== 'en_cours'
    && p.statut !== 'refuse'
    && p.valide !== false
    && p.heures_nettes
  );

  // Helper: taux en vigueur à une date donnée (respecte date_fin)
  // 🐛 2026-05-12 FIX BUG : punch d'un employé inactif affichait 0.00$ dans les stats du dossier.
  //   Cause : pour un employé sous-traitant / jamais finalisé en RH, `salaires.cout_employeur`
  //   et `salaires.hors_decret` peuvent être à 0. La cascade de fallback était trop courte.
  //   Fix : on étend la cascade à TOUS les salaires connus (commercial, résidentiel)
  //   ET si aucune entrée historique n'est active à la date du punch, on prend la
  //   DERNIÈRE entrée historique CONNUE (la plus récente, même expirée).
  const getCoutHoraireForPunch = (emp, punchDate) => {
    const historique = (emp?.historique_salaires || []).filter(h => h.date_effective);

    // Helper interne — récupère le 1er montant > 0 d'un objet salaires/historique
    const _firstPositiveCost = (obj) => {
      if (!obj) return 0;
      const candidates = [
        obj.cout_employeur,
        obj.nouveau_taux,
        obj.hors_decret,
        obj.commercial_industriel,
        obj.residentiel_leger,
      ];
      for (const c of candidates) {
        const v = parseFloat(c);
        if (!isNaN(v) && v > 0) return v;
      }
      return 0;
    };

    if (historique.length > 0) {
      // 1️⃣ Entrée historique active à la date du punch (respecte date_fin)
      const active = historique.filter(h => {
        const de = (h.date_effective || '').substring(0, 10);
        const df = (h.date_fin || '').substring(0, 10);
        return de && de <= punchDate && (!df || df >= punchDate);
      });
      if (active.length > 0) {
        const best = active.reduce((a, b) =>
          (a.date_effective || '') >= (b.date_effective || '') ? a : b);
        const cost = _firstPositiveCost(best);
        if (cost > 0) return cost;
      }
      // 2️⃣ Fallback : entrée historique la plus proche AVANT la date du punch
      const before = historique
        .filter(h => (h.date_effective || '').substring(0, 10) <= punchDate)
        .sort((a, b) => (b.date_effective || '').localeCompare(a.date_effective || ''));
      for (const h of before) {
        const cost = _firstPositiveCost(h);
        if (cost > 0) return cost;
      }
      // 3️⃣ Fallback : N'IMPORTE QUELLE entrée historique connue (même future)
      //    Utile quand l'employé inactif a été ajouté APRÈS la date du 1er punch.
      const sorted = [...historique].sort((a, b) =>
        (b.date_effective || '').localeCompare(a.date_effective || ''));
      for (const h of sorted) {
        const cost = _firstPositiveCost(h);
        if (cost > 0) return cost;
      }
    }
    // 4️⃣ Fallback final : salaires actuels (élargi à toutes les colonnes connues)
    return _firstPositiveCost(emp?.salaires);
  };

  /**
   * 🆕 2026-05-06 Module CCQ Phase 7 — Coût horaire RÉEL appliqué au punch.
   * Si le punch est sur un BT en régime CCQ ET que le snapshot contient le coût
   * main-d'œuvre tiré de la grille CCQ active au moment du punch → on l'utilise.
   * Sinon, fallback sur le calcul historique hors-CCQ (cout_employeur de la fiche).
   *
   * Cela garantit que les stats client reflètent les VRAIS coûts employeur :
   * 80,26 $/h pour un compagnon CCQ vs son taux hors-CCQ habituel.
   */
  const getCoutHoraireApplique = (p, emp) => {
    const snap = p?.ccq_snapshot;
    if (p?.bt_regime_ccq === 'ccq' && snap?.cout_main_oeuvre_horaire > 0) {
      return snap.cout_main_oeuvre_horaire;
    }
    const punchDate = (p.punch_in_time || p.date || '').substring(0, 10);
    return getCoutHoraireForPunch(emp, punchDate);
  };

  const coutMainOeuvre = completedPunches.reduce((sum, p) => {
    const emp = employees.find(e => e.id === p.employee_id);
    const coutHoraire = getCoutHoraireApplique(p, emp);
    // 🚚 [Phase v13.52 — 2026-07-20] Coût = taux × (heures_nettes + déplacement) (Q6a).
    const hDepl = (parseInt(p.temps_deplacement_minutes, 10) || 0) / 60;
    return sum + (coutHoraire * ((p.heures_nettes || 0) + hDepl));
  }, 0);
  // Ajouter les heures manuelles
  const coutHeuresManuelles = heuresManuelles.reduce((sum, h) => sum + (h.cout_total || 0), 0);
  const totalHeuresManuelles = heuresManuelles.reduce((sum, h) => sum + (h.heures || 0), 0);
  const coutMainOeuvreTotale = coutMainOeuvre + coutHeuresManuelles;
  const pctMainOeuvre = montantContrat > 0 ? (coutMainOeuvreTotale / montantContrat * 100) : 0;

  // Coût matériaux entrepôt
  const coutMateriaux = materiauxEntrepot.cout_total_materiaux || 0;
  const pctMateriaux = montantContrat > 0 ? (coutMateriaux / montantContrat * 100) : 0;

  // Coût total (inclut maintenant les matériaux entrepôt + frais manuels)
  const coutFraisMateriaux = fraisMateriaux.reduce((sum, f) => sum + (f.montant || 0), 0);
  const coutTotal = coutProduitsTotaux + coutMainOeuvreTotale + coutMateriaux + coutFraisMateriaux;
  const pctCoutTotal = montantContrat > 0 ? (coutTotal / montantContrat * 100) : 0;
  const pctFraisMateriaux = montantContrat > 0 ? (coutFraisMateriaux / montantContrat * 100) : 0;
  const marge = montantContrat - coutTotal;

  // Indicateurs de couleur basés sur les objectifs configurés
  const getMargeColor = (pct) => {
    if (pct >= objectifsMarge.objectif_marge_brute_ideal) return 'text-green-600 bg-green-50';
    if (pct >= objectifsMarge.objectif_marge_brute_min) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };
  const getCoutColor = (actual, target) => {
    if (actual <= target) return 'text-green-600';
    if (actual <= target * 1.2) return 'text-amber-600';
    return 'text-red-600';
  };
  const margePct = montantContrat > 0 ? (marge / montantContrat * 100) : 0;

  const fmt = (v) => v.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
  const pct = (v) => `${v.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      {/* 🆕 [Phase 53] Badge "Stats combinées" — affiché UNIQUEMENT quand le BT
          a des dossiers liés (jumelés). Indique clairement que les chiffres
          ci-dessous sont la somme des N dossiers liés. */}
      {isAggregated && (
        <Card className="border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="badge-stats-combinees">
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <span className="text-2xl shrink-0">📊</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-indigo-900">
                Stats combinées — {1 + dossiersLiesData.length} dossiers liés
              </div>
              <div className="text-xs text-indigo-700 mt-0.5 truncate">
                {[
                  `${dossier.client_prenom || ''} ${dossier.client_nom || ''}`.trim() || dossier.nom_dossier,
                  ...dossiersLiesData.map(d => d.nom),
                ].join(' + ')}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Catégorie du projet — Propriétaire seulement */}
      {isProprietaire && (
        <Card className="border-2 border-blue-100 bg-blue-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Catégorie du projet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Select value={categorieActuelle || '__aucune__'} onValueChange={saveCategorie} disabled={savingCategorie}>
                <SelectTrigger className="w-64" data-testid="dossier-categorie-select">
                  <SelectValue placeholder="Aucune catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__aucune__">Aucune catégorie</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.nom}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categorieActuelle && <Badge variant="outline" className="text-blue-700 border-blue-300">{categorieActuelle}</Badge>}
              {categories.length === 0 && <p className="text-xs text-gray-400">Gérer les catégories dans <strong>Analyse des Marges</strong></p>}
            </div>
          </CardContent>
        </Card>
      )}
      {!isProprietaire && categorieActuelle && (
        <div className="flex items-center gap-2 px-1">
          <Tag className="w-4 h-4 text-blue-500" />
          <span className="text-sm text-gray-600">Catégorie :</span>
          <Badge variant="outline" className="text-blue-700 border-blue-300">{categorieActuelle}</Badge>
        </div>
      )}

      {/* Financier */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-gray-500 mb-1">Valeur Contrat</p>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-600" />
              <p className="text-xl font-bold text-blue-600">{fmt(montantContrat)}</p>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Sans taxes (HT)</p>
          </CardContent>
        </Card>
        <StatCard label="Facturé" value={fmt(montantFacture)} icon={DollarSign} color="purple" />
        <StatCard label="Payé" value={fmt(montantPaye)} icon={DollarSign} color="green" />
        <StatCard label="Solde Dû" value={fmt(montantDu)} icon={DollarSign} color="red" />
      </div>

      {/* Production */}
      <Card>
        <CardHeader><CardTitle>Production</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-blue-600" /><span className="text-sm text-gray-600">Heures Budgétées</span></div>
              <p className="text-2xl font-bold">{heuresBudgetees}h</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-orange-600" /><span className="text-sm text-gray-600">Heures Réelles</span></div>
              <p className="text-2xl font-bold">{heuresReelles.toFixed(2).replace('.', ',')}h</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-green-600" /><span className="text-sm text-gray-600">Taux Réalisation</span></div>
              <p className="text-2xl font-bold">{pct(tauxRealisation)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Coûts vs Contrat */}
      <Card>
        <CardHeader><CardTitle>Rentabilité du Projet</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <CostRow label="Produits (peinture)" icon={Package} cost={coutProduitsTotaux} pct={pctProduits} color="amber" onClick={() => setShowDetailProduits(true)} />
            <CostRow label="Main d'œuvre (coût employeur)" icon={Users} cost={coutMainOeuvreTotale} pct={pctMainOeuvre} color="blue" onClick={() => setShowDetailMO(true)} />
            <CostRow label="Matériaux entrepôt" icon={Warehouse} cost={coutMateriaux} pct={pctMateriaux} color="purple" />
            {coutFraisMateriaux > 0 && (
              <CostRow label="Frais matériaux (manuels)" icon={Receipt} cost={coutFraisMateriaux} pct={pctFraisMateriaux} color="orange" />
            )}
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg">Coût Total</span>
                <div className="text-right">
                  <span className="font-bold text-lg">{fmt(coutTotal)}</span>
                  <span className={`ml-2 text-sm font-semibold ${pctCoutTotal > 70 ? 'text-red-600' : 'text-green-600'}`}>{pct(pctCoutTotal)} du contrat</span>
                </div>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="font-semibold text-green-700">Marge Brute</span>
                <div className={`text-right px-3 py-1 rounded-lg ${getMargeColor(margePct)}`}>
                  <span className="font-bold text-lg">{fmt(marge)}</span>
                  <span className="ml-2 text-sm font-semibold">{pct(margePct)}</span>
                  {montantContrat > 0 && (
                    <span className="ml-1 text-xs">
                      {margePct >= objectifsMarge.objectif_marge_brute_ideal ? '✅ Excellent' :
                       margePct >= objectifsMarge.objectif_marge_brute_min ? '⚠️ Acceptable' : '🔴 Insuffisant'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Barre visuelle */}
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex">
              <div className="bg-amber-500 h-full" style={{ width: `${Math.min(pctProduits, 100)}%` }} title={`Produits: ${pct(pctProduits)}`} />
              <div className="bg-blue-500 h-full" style={{ width: `${Math.min(pctMainOeuvre, 100)}%` }} title={`Main d'œuvre: ${pct(pctMainOeuvre)}`} />
              <div className="bg-purple-500 h-full" style={{ width: `${Math.min(pctMateriaux, 100)}%` }} title={`Matériaux: ${pct(pctMateriaux)}`} />
            </div>
            <div className="flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded" />Produits</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded" />Main d'œuvre</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded" />Matériaux</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-100 rounded border" />Marge</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Matériaux entrepôt détaillés */}
      {materiauxEntrepot.movements?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-purple-600" />
              Matériaux utilisés (Entrepôt)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {materiauxEntrepot.movements.map(m => (
                <div key={m.id} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium text-sm">{m.product_name}</p>
                    <p className="text-xs text-gray-500">{m.date} · {m.taken_by && `Par: ${m.taken_by}`}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="mr-2">{m.quantity} unité(s)</Badge>
                    <span className="text-sm font-semibold text-purple-600">
                      {fmt(m.quantity * (m.unit_cost || 0))}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 font-bold">
                <span>Total matériaux</span>
                <span className="text-purple-600">{fmt(coutMateriaux)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Heures Manuelles (Historique) — SECTION RETIRÉE (2026-04-20) — Remplacée par le nouveau système de saisie via BT. Les données existantes restent comptabilisées dans les totaux. */}

      {/* Produits Manuels (Historique) — SECTION RETIRÉE (2026-04-20) — Remplacée par le nouveau système de commandes via BT. Les données existantes restent comptabilisées dans "Produits (peinture)". */}

      {/* Frais matériaux manuels */}
      <Card>
        <CardHeader className="cursor-pointer" onClick={() => setShowFraisSection(prev => !prev)}>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-orange-500" />
              Frais Matériaux Manuels
              {fraisMateriaux.length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">{fraisMateriaux.length} frais · {fmt(coutFraisMateriaux)}</Badge>
              )}
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showFraisSection ? 'rotate-180' : ''}`} />
            </CardTitle>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowAddFrais(true); }} data-testid="add-frais-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </CardHeader>
        {showFraisSection && (
        <CardContent>
          {fraisMateriaux.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Aucun frais matériaux enregistré.</p>
          ) : (
            <div className="space-y-2">
              {fraisMateriaux.map(f => (
                <div key={f.id} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium text-sm">{f.description || 'Frais matériaux'}</p>
                    <p className="text-xs text-gray-500">{f.date}</p>
                    {f.facture_url && (
                      <a href={f.facture_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 underline">Voir la facture</a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-orange-600">{fmt(f.montant || 0)}</span>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => deleteFrais(f.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 font-bold">
                <span>Total frais matériaux</span>
                <span className="text-orange-600">{fmt(coutFraisMateriaux)}</span>
              </div>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Calendrier */}
      <Card>
        <CardHeader><CardTitle>Informations Générales</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Soumissions:</span> <strong>{soumissions.length}</strong></div>
            <div><span className="text-gray-500">Factures:</span> <strong>{factures.length}</strong></div>
            <div><span className="text-gray-500">Bons de travail:</span> <strong>{bonsTravail.length}</strong></div>
            <div><span className="text-gray-500">Pointages:</span> <strong>{completedPunches.length}</strong></div>
            <div><span className="text-gray-500">Représentant:</span> <strong>{dossier.representant_nom || 'Non assigné'}</strong></div>
            <div><span className="text-gray-500">Chef d'Équipe:</span> <strong>{dossier.chef_equipe_nom || 'Non assigné'}</strong></div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Détail Produits */}
      <Dialog open={showDetailProduits} onOpenChange={setShowDetailProduits}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Détail Produits (peinture) — {dossier.client_nom || dossier.nom_dossier}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Produits des BTs */}
            {commandesBonsTravail.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Commandes des bons de travail</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50 text-xs"><th className="text-left py-2 px-2">Produit</th><th className="text-right py-2">Qté</th><th className="text-right py-2">Prix unit.</th><th className="text-right py-2 px-2">Total</th></tr></thead>
                  <tbody>
                    {commandesBonsTravail.map((p, i) => {
                      // Récupérer le prix D'ACHAT depuis la table produits (prix payé au fournisseur)
                      const produitData = produits.find(pr => pr.id === p.produit_id);
                      // 🐛 [Phase 94 — 2026-07-08] Exception E3 : priorité snapshot commande
                      const prixUnitaire = parseFloat(p.prix_paye_snapshot || p.prix_paye || p.prix_unitaire || produitData?.prix_paye || 0);
                      const quantite = parseFloat(p.quantite_commandee || p.quantite || 0);
                      const total = prixUnitaire * quantite;
                      
                      return (
                        <tr key={p.id || p._id || `${JSON.stringify(p)}-${i}`} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-2 font-medium">{p.produit_nom || p.nom || '—'}</td>
                          <td className="text-right py-2">{quantite}</td>
                          <td className="text-right py-2">{prixUnitaire.toFixed(2)}$</td>
                          <td className="text-right py-2 px-2 font-semibold">{total.toFixed(2)}$</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={3}>Sous-total commandes</td><td className="text-right py-2 px-2 text-amber-700">{coutCommandesBons.toFixed(2)}$</td></tr></tfoot>
                </table>
              </div>
            )}
            {/* Produits manuels */}
            {produitsManuels.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Produits manuels (historique)</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50 text-xs"><th className="text-left py-2 px-2">Produit</th><th className="text-right py-2">Qté</th><th className="text-right py-2 px-2">Coût</th></tr></thead>
                  <tbody>
                    {produitsManuels.map((p, i) => (
                      <tr key={p.id || p._id || `${JSON.stringify(p)}-${i}`} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium">{p.produit_nom || '—'}</td>
                        <td className="text-right py-2">{p.quantite || 0}</td>
                        <td className="text-right py-2 px-2 font-semibold">{(p.cout_total || 0).toFixed(2)}$</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="font-bold border-t-2"><td className="py-2 px-2" colSpan={2}>Sous-total manuels</td><td className="text-right py-2 px-2 text-amber-700">{coutProduitsManuels.toFixed(2)}$</td></tr></tfoot>
                </table>
              </div>
            )}
            {commandesBonsTravail.length === 0 && produitsManuels.length === 0 && (
              <p className="text-slate-400 italic text-center py-4">Aucun produit enregistré pour ce dossier.</p>
            )}
            <div className="border-t pt-3 flex justify-between font-bold text-lg">
              <span>Total Produits</span>
              <span className="text-amber-700">{fmt(coutProduitsTotaux)}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Détail Main d'oeuvre */}
      <Dialog open={showDetailMO} onOpenChange={setShowDetailMO}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Détail Main d'oeuvre — {dossier.client_nom || dossier.nom_dossier}
            </DialogTitle>
          </DialogHeader>
          {/* 🗑️ 2026-05-13 — Bouton « Ajouter des heures (sans punch) » supprimé :
              les heures sont désormais saisies UNIQUEMENT via Validation des heures
              → Importer des heures (canal unique, traçable, lié aux punchs). Les
              entrées manuelles historiques restent visibles ci-dessous. */}
          <div className="space-y-4">
            {/* Punches réels */}
            {completedPunches.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Pointages du chantier</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50 text-xs"><th className="text-left py-2 px-2">Employé</th><th className="text-right py-2">Date</th><th className="text-center py-2">Régime</th><th className="text-right py-2">Heures</th><th className="text-right py-2 px-2">Coût employeur</th></tr></thead>
                  <tbody>
                    {completedPunches.map((p, i) => {
                      const emp = employees.find(e => e.id === p.employee_id);
                      const coutH = getCoutHoraireApplique(p, emp);
                      const isCCQ = p.bt_regime_ccq === 'ccq' && p.ccq_snapshot?.cout_main_oeuvre_horaire > 0;
                      // 🐛 2026-05-12 — Détecte un coût horaire manquant pour ce punch
                      //   (typique : employé inactif / sous-traitant sans cout_employeur configuré).
                      const coutManquant = coutH <= 0 && (p.heures_nettes || 0) > 0;
                      return (
                        <tr key={`stat-${i}`} className="border-b hover:bg-slate-50" data-testid={`punch-row-${p.id || i}`}>
                          <td className="py-2 px-2 font-medium">
                            {p.employee_nom || '—'}
                            {emp?.est_actif === false && (
                              <span className="ml-1 text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1 py-0.5" title="Employé inactif">inactif</span>
                            )}
                          </td>
                          <td className="text-right py-2 text-xs text-slate-500">{(p.punch_in_time || '').substring(0, 10)}</td>
                          <td className="text-center py-2">
                            <RegimeBadge regime={p.bt_regime_ccq} requiresReview={p.ccq_snapshot?.requires_ccq_grade_review === true} />
                          </td>
                          <td className="text-right py-2">{(p.heures_nettes || 0).toFixed(2)}h</td>
                          <td className={`text-right py-2 px-2 font-semibold ${isCCQ ? 'text-violet-700' : coutManquant ? 'text-red-600' : ''}`}
                              title={coutManquant
                                ? `⚠ Aucun coût employeur configuré pour ${p.employee_nom || 'cet employé'}. Mettre à jour sa fiche pour comptabiliser le coût réel.`
                                : isCCQ
                                  ? `Taux CCQ: ${coutH.toFixed(2)} $/h (grille du ${p.ccq_snapshot?.ccq_grille_date_effet || '?'})`
                                  : `Taux hors-CCQ: ${coutH.toFixed(2)} $/h`}>
                            {coutManquant ? (
                              <span className="inline-flex items-center gap-1" data-testid={`badge-cout-manquant-${p.id || i}`}>
                                <span className="text-[10px] bg-red-50 border border-red-200 text-red-700 rounded px-1.5 py-0.5">⚠ Taux manquant</span>
                                <span>0.00$</span>
                              </span>
                            ) : (
                              <>{(coutH * (p.heures_nettes || 0)).toFixed(2)}$</>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr className="font-bold border-t-2">
                    <td className="py-2 px-2" colSpan={3}>Sous-total pointages</td>
                    <td className="text-right py-2">{completedPunches.reduce((s,p) => s+(p.heures_nettes||0),0).toFixed(2)}h</td>
                    <td className="text-right py-2 px-2 text-blue-700">{coutMainOeuvre.toFixed(2)}$</td>
                  </tr></tfoot>
                </table>
              </div>
            )}
            {/* Heures manuelles */}
            {heuresManuelles.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Heures manuelles (historique)</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50 text-xs"><th className="text-left py-2 px-2">Employé</th><th className="text-right py-2">Date</th><th className="text-right py-2">Heures</th><th className="text-right py-2 px-2">Coût total</th></tr></thead>
                  <tbody>
                    {heuresManuelles.map((h, i) => (
                      <tr key={h.id || h._id || `${JSON.stringify(h)}-${i}`} className="border-b hover:bg-slate-50">
                        <td className="py-2 px-2 font-medium">{h.employee_nom || '—'}</td>
                        <td className="text-right py-2 text-xs text-slate-500">{h.date || '—'}</td>
                        <td className="text-right py-2">{(h.heures || 0).toFixed(2)}h</td>
                        <td className="text-right py-2 px-2 font-semibold">{(h.cout_total || 0).toFixed(2)}$</td>
                        <td className="text-right py-2 px-2">
                          {h.id && (
                            <button
                              onClick={() => deleteHeuresManuelles(h.id)}
                              title="Supprimer"
                              className="text-red-500 hover:text-red-700"
                              data-testid={`btn-delete-heure-manuelle-${h.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="font-bold border-t-2">
                    <td className="py-2 px-2" colSpan={2}>Sous-total manuelles</td>
                    <td className="text-right py-2">{totalHeuresManuelles.toFixed(2)}h</td>
                    <td className="text-right py-2 px-2 text-blue-700">{coutHeuresManuelles.toFixed(2)}$</td>
                  </tr></tfoot>
                </table>
              </div>
            )}
            {completedPunches.length === 0 && heuresManuelles.length === 0 && (
              <p className="text-slate-400 italic text-center py-4">Aucune heure enregistrée pour ce dossier.</p>
            )}
            <div className="border-t pt-3 flex justify-between font-bold text-lg">
              <span>Total Main d'oeuvre</span>
              <span className="text-blue-700">{fmt(coutMainOeuvreTotale)}</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Ajouter Frais */}
      <Dialog open={showAddFrais} onOpenChange={setShowAddFrais}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="w-4 h-4" />Ajouter un frais matériaux</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant ($) *</Label>
                <Input type="number" step="0.01" min="0" value={fraisForm.montant} onChange={(e) => setFraisForm(f => ({ ...f, montant: e.target.value }))} placeholder="0.00" data-testid="frais-montant-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={fraisForm.date} onChange={(e) => setFraisForm(f => ({ ...f, date: e.target.value }))} data-testid="frais-date-input" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={fraisForm.description} onChange={(e) => setFraisForm(f => ({ ...f, description: e.target.value }))} placeholder="Quincaillerie, accessoires..." data-testid="frais-description-input" />
            </div>
            <div>
              <Label>Photo de la facture</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*,application/pdf" onChange={uploadFacture} disabled={uploadingFacture} className="text-sm" data-testid="frais-facture-upload" />
                {uploadingFacture && <span className="text-xs text-gray-500">Upload...</span>}
                {fraisForm.facture_url && <span className="text-xs text-green-600">Téléversée</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFrais(false)}>Annuler</Button>
            <Button onClick={addFrais} disabled={savingFrais} data-testid="save-frais-btn">
              {savingFrais ? 'Sauvegarde...' : 'Ajouter le frais'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🗑️ 2026-05-13 — Dialog « Ajouter Heures Manuelles » supprimé. Le canal
          d'entrée unique est désormais Validation des heures → Importer des heures. */}

      {/* Dialog Ajouter Produit Manuel */}
      <Dialog open={showAddProduit} onOpenChange={setShowAddProduit}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-4 h-4 text-amber-500" />Ajouter un produit manuel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Produit *</Label>
              <Select value={produitForm.produit_id} onValueChange={(val) => setProduitForm(f => ({ ...f, produit_id: val }))}>
                <SelectTrigger data-testid="produit-select"><SelectValue placeholder="Sélectionner un produit..." /></SelectTrigger>
                <SelectContent>
                  {produits.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nom || p.name} — {fmt(p.prix_paye || 0)}/unité
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantité *</Label>
                <Input type="number" step="0.5" min="0" value={produitForm.quantite} onChange={(e) => setProduitForm(f => ({ ...f, quantite: e.target.value }))} placeholder="10" data-testid="produit-quantite-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={produitForm.date} onChange={(e) => setProduitForm(f => ({ ...f, date: e.target.value }))} data-testid="produit-date-input" />
              </div>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Input value={produitForm.notes} onChange={(e) => setProduitForm(f => ({ ...f, notes: e.target.value }))} placeholder="Ex: Benjamin Moore Regal Select..." data-testid="produit-notes-input" />
            </div>
            {produitForm.produit_id && produitForm.quantite && (() => {
              const prod = produits.find(p => p.id === produitForm.produit_id);
              const ppu = prod?.prix_paye || 0;
              return (
                <div className="bg-amber-50 rounded-lg p-3 text-sm">
                  <p className="text-amber-800">Coût estimé : <strong>{fmt(parseFloat(produitForm.quantite) * ppu)}</strong></p>
                  <p className="text-amber-600 text-xs">({produitForm.quantite} × {fmt(ppu)})</p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProduit(false)}>Annuler</Button>
            <Button onClick={addProduitManuel} disabled={savingProduit} data-testid="save-produit-btn">
              {savingProduit ? 'Sauvegarde...' : 'Ajouter le produit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = { blue: 'text-blue-600', purple: 'text-purple-600', green: 'text-green-600', red: 'text-red-600' };
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colors[color]}`} />
          <p className={`text-xl font-bold ${colors[color]}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function CostRow({ label, icon: Icon, cost, pct, color, onClick }) {
  const fmt = (v) => v.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
  const colors = { amber: 'text-amber-600', blue: 'text-blue-600', green: 'text-green-600', purple: 'text-purple-600', orange: 'text-orange-500' };
  return (
    <div
      className={`flex justify-between items-center ${onClick ? 'cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-1 rounded-lg transition-colors group' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${colors[color]}`} />
        <span className={`text-sm font-medium ${onClick ? 'group-hover:text-blue-600 group-hover:underline' : ''}`}>{label}</span>
        {onClick && <span className="text-xs text-slate-400 group-hover:text-blue-500">(voir détail)</span>}
      </div>
      <div className="text-right">
        <span className="font-semibold">{fmt(cost)}</span>
        <span className="ml-2 text-sm text-gray-500">{pct.toFixed(1)}%</span>
      </div>
    </div>
  );
}
